import React, { useEffect, useMemo } from 'react';
import { InstantSearch, Configure } from 'react-instantsearch';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { relationshipCompanySearch } from '@/utils/typesense/clients/relationshipCompanySearch';
import { joinFilters } from '@/utils/constants';
import { TableRefresh } from '@/components/table/TableRefresh';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import { DEFAULT_HIDDEN_COLUMNS, relationshipCompanyHeadCells } from './data';
import RelationshipCompaniesTableFilters from './RelationshipCompaniesTableFilters';
import {
	changeRowsPerPage,
	onOrderChange,
	setOrder,
	setOrderBy,
} from '@/features/table/relationshipCompaniesTableSlice';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';

const RelationshipCompaniesTable = () => {
	const dispatch = useDispatch();
	const { pathname: slug } = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const brokerGroupIds = userDetails?.brokerGroupArray ?? [];

	const { rowsPerPage, orders } = useSelector((state) => state.relationshipCompaniesTable);
	const orderParams = useMemo(
		() => orders[slug] ?? { order: 'desc', orderBy: 'lastUpdated' },
		[orders, slug],
	);
	const { order, orderBy } = orderParams;
	const { ownerFilter } = useInstantSearchOwnerFilters();

	useEffect(() => {
		if (orders[slug]?.orderBy === 'lastUpdated' && orders[slug]?.order === 'desc') {
			return;
		}
		dispatch(setOrderBy({ slug, orderBy: orderBy ?? 'lastUpdated' }));
		dispatch(setOrder({ slug, order: order ?? 'desc' }));
	}, [dispatch, order, orderBy, orders, slug]);

	useEffect(() => {
		dispatch(onOrderChange());
	}, [dispatch, orderBy, order]);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const typesenseFilters = useMemo(() => {
		return joinFilters([
			brokerGroupIds.length > 0 ? `brokerGroupId:[${brokerGroupIds.join(',')}]` : '',
			ownerFilter,
		]);
	}, [brokerGroupIds, ownerFilter]);

	const columns = useMemo(() => parseHeadCells(relationshipCompanyHeadCells), []);

	return (
		<InstantSearch indexName='relationshipCompanies' searchClient={relationshipCompanySearch}>
			<Configure filters={typesenseFilters} />
			<TableRefresh table='relationshipCompaniesTable' />
			<RelationshipCompaniesTableFilters />
			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType='relationshipCompany'
				columns={columns}
				tab='companies'
				variant={slug}
				enableExport={false}
				showPreview={false}
				defaultHiddenColumns={DEFAULT_HIDDEN_COLUMNS}
			/>
		</InstantSearch>
	);
};

RelationshipCompaniesTable.displayName = 'RelationshipCompaniesTable';

export default RelationshipCompaniesTable;
